import * as tus from 'tus-js-client';
import {
  SITE_SETTINGS_MEDIA_ALLOWED_TYPES,
  SITE_SETTINGS_MEDIA_BUCKET,
  SITE_SETTINGS_MEDIA_MAX_BYTES,
  SITE_SETTINGS_MEDIA_PUBLIC_BASE_PATH,
} from '@/lib/constants/siteSettingsMedia';
import { supabase } from '@/lib/supabase/client';

export interface SiteSettingsMediaUploadResult {
  url: string;
  storagePath: string;
  fileName: string;
  fileSize: number;
  contentType: string | null;
}

interface UploadSiteSettingsMediaParams {
  file: File;
  onProgress?: (progress: number) => void;
}
interface UploadResumableParams {
  file: File;
  objectPath: string;
  contentType: string | null;
  onProgress?: (progress: number) => void;
}

const RESUMABLE_CHUNK_SIZE = 6 * 1024 * 1024;
const RESUMABLE_RETRY_DELAYS = [0, 3000, 5000, 10000, 20000];

function createMediaIdentifier(): string {
  if (
    typeof globalThis.crypto !== 'undefined' &&
    typeof globalThis.crypto.randomUUID === 'function'
  ) {
    return globalThis.crypto.randomUUID();
  }
  return Math.random().toString(36).slice(2, 10);
}

function sanitizeFileName(fileName: string): string {
  const baseName = fileName.split('.').slice(0, -1).join('.') || fileName;
  const normalized = baseName
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/-{2,}/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 32)
    .trim();
  return normalized.length > 0 ? normalized : 'media';
}

function extractExtension(fileName: string): string {
  const parts = fileName.split('.');
  if (parts.length <= 1) {
    return '';
  }
  const ext = parts.pop();
  if (typeof ext === 'string' && ext.length > 0) {
    return `.${ext.toLowerCase()}`;
  }
  return '';
}

function buildObjectPath(file: File): string {
  const identifier = createMediaIdentifier();
  const slug = sanitizeFileName(file.name);
  const extension = extractExtension(file.name);
  return `${SITE_SETTINGS_MEDIA_PUBLIC_BASE_PATH}/${identifier}-${slug}${extension}`;
}

function getStorageResumableEndpoint(): string {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  if (typeof supabaseUrl !== 'string' || supabaseUrl.length === 0) {
    throw new Error('Missing Supabase environment variable VITE_SUPABASE_URL.');
  }
  const baseUrl = new URL(supabaseUrl);
  let host = baseUrl.hostname;
  if (!host.includes('storage.supabase.') && host.includes('supabase.')) {
    host = host.replace('supabase.', 'storage.supabase.');
  }
  return `${baseUrl.protocol}//${host}/storage/v1/upload/resumable`;
}

async function getAccessToken(): Promise<string> {
  const { data, error } = await supabase.auth.getSession();
  if (error) {
    throw error;
  }
  const token = data.session?.access_token ?? '';
  if (token.length === 0) {
    throw new Error('You must be signed in to upload media.');
  }
  return token;
}

export function isAllowedSiteSettingsMediaType(file: File): boolean {
  if (SITE_SETTINGS_MEDIA_ALLOWED_TYPES.length === 0) {
    return true;
  }
  return SITE_SETTINGS_MEDIA_ALLOWED_TYPES.includes(file.type);
}

export function isWithinSiteSettingsMediaSizeLimit(file: File): boolean {
  if (!SITE_SETTINGS_MEDIA_MAX_BYTES) {
    return true;
  }
  return typeof file.size === 'number' && file.size <= SITE_SETTINGS_MEDIA_MAX_BYTES;
}

async function uploadFileResumable({
  file,
  objectPath,
  contentType,
  onProgress,
}: UploadResumableParams): Promise<void> {
  const accessToken = await getAccessToken();
  const endpoint = getStorageResumableEndpoint();

  await new Promise<void>((resolve, reject) => {
    const upload = new tus.Upload(file, {
      endpoint,
      retryDelays: RESUMABLE_RETRY_DELAYS,
      chunkSize: RESUMABLE_CHUNK_SIZE,
      uploadDataDuringCreation: true,
      removeFingerprintOnSuccess: true,
      headers: {
        authorization: `Bearer ${accessToken}`,
        'x-upsert': 'false',
      },
      metadata: {
        bucketName: SITE_SETTINGS_MEDIA_BUCKET,
        objectName: objectPath,
        contentType: contentType ?? 'application/octet-stream',
        cacheControl: '3600',
      },
      onError: (error) => {
        reject(error);
      },
      onProgress: (bytesUploaded, bytesTotal) => {
        if (!bytesTotal) {
          return;
        }
        const percent = Math.round((bytesUploaded / bytesTotal) * 100);
        const clamped = Math.min(100, Math.max(0, percent));
        onProgress?.(clamped);
      },
      onSuccess: () => {
        onProgress?.(100);
        resolve();
      },
    });

    upload
      .findPreviousUploads()
      .then((previousUploads) => {
        if (previousUploads.length > 0) {
          upload.resumeFromPreviousUpload(previousUploads[0]);
        }
        upload.start();
      })
      .catch(reject);
  });
}

export async function uploadSiteSettingsMediaFile({
  file,
  onProgress,
}: UploadSiteSettingsMediaParams): Promise<SiteSettingsMediaUploadResult> {
  if (!isWithinSiteSettingsMediaSizeLimit(file)) {
    const maxSizeGb = Math.round(SITE_SETTINGS_MEDIA_MAX_BYTES / (1024 * 1024 * 1024));
    throw new Error(`Files must be under ${maxSizeGb.toString()}GB (Supabase limit).`);
  }
  const objectPath = buildObjectPath(file);
  const contentType = typeof file.type === 'string' && file.type.length > 0 ? file.type : null;
  await uploadFileResumable({ file, objectPath, contentType, onProgress });

  const { data: publicUrlData } = supabase.storage
    .from(SITE_SETTINGS_MEDIA_BUCKET)
    .getPublicUrl(objectPath);
  const url = publicUrlData.publicUrl;

  return {
    storagePath: `${SITE_SETTINGS_MEDIA_BUCKET}/${objectPath}`,
    url,
    fileName: file.name,
    fileSize: typeof file.size === 'number' ? file.size : 0,
    contentType,
  };
}
